import { AxiosRequestConfig } from "axios";
import { RootState } from "../App/store";
import { OrderSliceState } from "../Features/BCSOrder/BCSOrderSlice";
import { FileResponse } from "../Requests/portfolioRequests";
import { ISearchParameters, ITableReport, Report } from "./TableInterfaces";
import {
  AddOrderFormValues,
  CallOptionDropDownItem,
} from "./callOptionsInterfaces";
import { Packet } from "./commonDtos";
import { DropdownItem } from "./dropdownInterfaces";
import { TradeTableReport } from "./tradeTableInterfaces";

export interface GetOrderBookPairValues {
  insCodePurchase?: string;
  insCodeSell?: string;
}

export interface GetBookOrderResponse {
  lastUpdateStr: string | null;
  purchaseOrderbook: OrderBook;
  sellOrderbook: OrderBook;
  generalInfo: GeneralInfo;
  calculatedInfo: CalculatedInfo;
}

export interface CalculatedInfo {
  deltaP: number;
  deltaOrderBook: number;
  profit: number;
  profitPercent: number;
}

export interface BestLimits extends ITableReport {
  number: number | null;
  qTitMeDem: number | null;
  zOrdMeDem: number | null;
  pMeDem: number | null;
  pMeOf: number | null;
  zOrdMeOf: number | null;
  qTitMeOf: number | null;
  insCode: number | null;
}

export interface OrderBookPriceRange {
  minPricePermit?: number | null;
  maxPricePermit?: number | null;
}

export interface OrderBook extends OrderBookPriceRange {
  fullName: string;
  namad: string;
  insId: string;
  instrumentId?: string;
  /** Client-only marker added when this snapshot arrives through SignalR. */
  wsUpdateSequence?: number;
  samplingTime?: string | null;
  lastUpdateTime?: string | null;
  lastPrice?: number | null;
  closingPrice?: number | null;
  minPricePermit?: number | null;
  maxPricePermit?: number | null;
  tseLink: string;
  baseNamad: string;
  price: number;
  dueDate: string;
  bestLimitsDto: {
    bestLimits: Array<BestLimits>;
  };
}

export interface GeneralInfo {
  dueDate: string;
  baseNamad: string;
  remainingDays: string | null;
}

export interface OrderTableSearchParam extends ISearchParameters {
  sahraUserId?: number;
  baseInsCode?: Array<string>;
}

export interface OrderTableReport extends ITableReport {
  methodName: string;
  orderStrategy: number;
  shekarOrderId: number;
  sahraUserId: number;
  sahraName: string;
  operator: string;
  namad: string;
  totalSize: number;
  buyVolume: number;
  sellVolume: number;
  pendingBuy: number;
  pendingSell: number;
  buyPrice: number;
  sellPrice: number;
  maxPairPosition: number;
  imBalance: number;
  blockSize: number;
  priority: string;
  permiumrisk: string;
  sellInstrument: string;
  buyInstrument: string;
  orderStatus: OrderStatus;
  isActive: boolean;
}

export enum OrderStrategy {
  Hunt = 1,
  One_sided_Competition = 2,
  Two_sided_Competition = 3,
  Async = 4,
  StepCompetition = 5,
  Upair = 6,
  NLeg = 7,
}

export interface ShekarChanceTableSearchParam {
  shekarOrderId: number;
}

export interface ShekarChanceReport extends ITableReport {
  totalSize: number;
  orderSize: number;
  date: string;
  purchaseOrderbookDelayInMs: number;
  sellOrderbookDelayInMs: number;
  chanceCreationDelay: number;
}

export enum OrderStatus {
  Inserted = 0,
  Doing = 1,
  Done = 2,
  Cancelled = 3,
}

export interface OrderRow {
  row: number;
  sahraUserId: number;
  orderStartegy: number;
  buyInsCode: string;
  buyInsName: string;
  sellInsCode: string;
  sellInsName: string;
  maxPositionPair: number;
  blockSize: number;
  volume: number;
  username: string;
}

export interface ManualOrderRow extends OrderRow {
  autoSellFund: boolean;
  scheduledExecutionTime?: string | null;
}

export interface FileOrderTableReport extends ITableReport {
  orderRow: OrderRow;
  row: number;
  id: number;
  username: string;
  orderStartegyStr: string;
  buyInsName: string;
  sellInsName: string;
  maxPositionPair: number;
  blockSize: number;
  volume: number;
}

export interface itemError {
  row: number;
  msg: string;
}

export interface ErrorTableReport extends ITableReport {
  id: number;
  itemError: itemError;
  row: number;
  username: string;
}

export enum OrderType {
  BCSOrder = 1,
  CoveredCallOrder = 2,
  ReplacementCoveredCall = 3,
  ReplacementBCS = 4,
}

export interface OrderErrorTableReport extends ITableReport {
  errorCode?: string;
}

export interface IFailedOrderTableReport extends ITableReport {
  userName: string;
  instrumentName: string;
  orderSideStr: string;
  originalVolumeInput: string;
  volumeCalcMethod: string;
  fromPrice: number;
  toPrice: number;
  previewMessage: string;
  scheduledExecutionTimeStr: string;
  isResentSuccessfully: boolean;
  lastSeenDateStr: string;
}

export interface IFailedOrderBulkPreviewItem<TPreview> {
  id: number;
  success: boolean;
  errorMessage?: string;
  preview?: TPreview;
}

export interface IFailedOrderBulkPreviewResponse<TPreview> {
  items: IFailedOrderBulkPreviewItem<TPreview>[];
  volumeSummary: any[];
}

export interface IFailedOrderResendResult {
  id: number;
  success: boolean;
  msg: string;
}

export interface IOrderFunctions {
  getSlice: () => OrderSliceState | undefined;
  getFormValues: () => AddOrderFormValues | null;
  addOrderFormValuesChangeHandler: (
    name: string,
    value: any,
    name2?: string,
    value2?: any
  ) => void;
  setAddOrderFormValues: (formValues: AddOrderFormValues | null) => void;
  updateOrderTable: () => void;
  stopOrderTableUpdate: () => void;
  updateTradeTable: () => void;
  stopTradeTableUpdate: () => void;
  getBuyInsCodeDropdown: () => JSX.Element;
  getSellInsCodeDropdown: () => JSX.Element;
  getOrderBookPair: (
    values: GetOrderBookPairValues
  ) => Promise<GetBookOrderResponse>;
  addShekarOrder: (formValues: AddOrderFormValues) => Promise<Packet<boolean>>;
  getOrderTable: (
    searchParam: OrderTableSearchParam,
    offset: number | null,
    pageSize: number | null,
    sortingType: string,
    additional?: DropdownItem,
    axiosOptions?: AxiosRequestConfig
  ) => Promise<Report<OrderTableReport>>;
  getShekarChanceTable: (
    searchParam: ShekarChanceTableSearchParam,
    offset: number | null,
    pageSize: number | null
  ) => Promise<Report<ShekarChanceReport>>;
  cancelOrder: (orderId: number) => Promise<Packet<boolean>>;
  uploadOrderFile: (
    searchParam: FormData | null,
    offset: number | null,
    pageSize: number | null
  ) => Promise<Report<FileOrderTableReport>>;
  orderBulkCancel: (rowIds: Array<number>) => Promise<Packet<boolean>>;
  addBulkOrder: (
    orderRows: Array<OrderRow> | null
  ) => Promise<Report<ErrorTableReport>>;
  refreshOrder: (id: number) => Promise<Packet<boolean>>;
  getCallOptions: (
    isPurchase: boolean,
    callOption?: string | number
  ) => Promise<Array<CallOptionDropDownItem>>;
  getTradeTable: (
    searchParam: OrderTableSearchParam,
    offset: number | null,
    pageSize: number | null,
    sortingType: string,
    additional?: DropdownItem,
    axiosOptions?: AxiosRequestConfig
  ) => Promise<Report<TradeTableReport>>;
  resumeOrder: (orderId: number) => Promise<Packet<boolean>>;
  bulkResumeOrder: (selectedRowsIds: number[]) => Promise<Packet<boolean>>;
  pauseOrder: (orderId: number) => Promise<Packet<boolean>>;
  bulkPauseOrder: (selectedRowsIds: number[]) => Promise<Packet<boolean>>;
  getErrorsTable: (
    searchParam: OrderTableSearchParam,
    offset: number | null,
    pageSize: number | null
  ) => Promise<Report<OrderErrorTableReport>>;
  getExcelFile: (searchParam: OrderTableSearchParam) => Promise<FileResponse>;
  getAggregateExcelFile: (
    searchParam: OrderTableSearchParam
  ) => Promise<FileResponse>;
}
